/* eslint-disable @typescript-eslint/no-explicit-any */
import i18next, { changeLanguage } from "i18next";
import resourcesToBackend from "i18next-resources-to-backend";

import { createMemo, createRoot, createSignal } from "solid-js";

import { getLocaleFromPath, updateUrlLocale } from "../lib/language.ts";
import { DefaultLocale, Locale, LocaleMap, langConf } from "./config.ts";

// 服务端感知的 locale 获取函数
function getServerSideLocale(): Locale {
  // 在服务端，我们需要从请求上下文中获取语言
  if (typeof window === "undefined") {
    // 服务端：从全局变量获取语言（由 entry-server 设置）
    const serverLocale = (globalThis as Record<string, unknown>)
      .__SERVER_LOCALE__;
    if (
      serverLocale &&
      typeof serverLocale === "string" &&
      Object.keys(LocaleMap).includes(serverLocale)
    ) {
      return serverLocale as Locale;
    }
    return DefaultLocale;
  }

  // 客户端：从 URL 或 cookie 获取语言
  const urlLocale = getLocaleFromPath(window.location.pathname);
  if (urlLocale && Object.keys(LocaleMap).includes(urlLocale)) {
    return urlLocale;
  }

  // 检查 cookie
  const cookieHeader = document.cookie;
  const cookieLang = cookieHeader
    ? Object.fromEntries(
        cookieHeader.split("; ").map((cookie) => {
          const [key, value] = cookie.split("=");
          return [key, value];
        }),
      )["i18n"]
    : null;

  if (cookieLang && Object.keys(LocaleMap).includes(cookieLang)) {
    return cookieLang as Locale;
  }

  return DefaultLocale;
}

// 获取当前语言
export function getLocale(): Locale {
  return currentLocale();
}

// 获取语言key
export function getLangKey(): number {
  return LocaleMap[currentLocale()];
}

// 存储当前语言 - 使用服务端感知的初始化
const [currentLocale, setCurrentLocale] = createSignal<Locale>(
  getServerSideLocale(),
);

// 服务端初始化函数 - 在 entry-server 中调用
export function initializeServerLocale(locale: Locale): void {
  if (typeof window === "undefined") {
    // 只在服务端设置
    setCurrentLocale(locale);
  }
}

// 获取服务端语言（用于 API 调用等）- 每次都重新获取，不使用缓存
export function getServerLocale(): Locale {
  if (typeof window === "undefined") {
    // 服务端：每次都重新从全局变量获取，确保获取当前请求的语言
    const serverLocale = (globalThis as Record<string, unknown>)
      .__SERVER_LOCALE__;
    if (
      serverLocale &&
      typeof serverLocale === "string" &&
      Object.keys(LocaleMap).includes(serverLocale)
    ) {
      return serverLocale as Locale;
    }
    return DefaultLocale;
  }
  return currentLocale();
}

// 获取服务端语言 key（用于 API 调用等）
export function getServerLangKey(): number {
  const locale = getServerLocale();
  return LocaleMap[locale];
}

// 服务端 API 调用时的语言参数获取
export function getServerLangParams(): { lang: number; locale: Locale } {
  const locale = getServerLocale();
  return {
    lang: LocaleMap[locale],
    locale,
  };
}

// 强制刷新服务端语言设置（用于确保获取最新语言）
export function refreshServerLocale(): void {
  if (typeof window === "undefined") {
    const serverLocale = (globalThis as Record<string, unknown>)
      .__SERVER_LOCALE__;
    if (
      serverLocale &&
      typeof serverLocale === "string" &&
      Object.keys(LocaleMap).includes(serverLocale)
    ) {
      setCurrentLocale(serverLocale as Locale);
    }
  }
}

// 清除服务端语言缓存（用于确保每次请求都重新获取）
export function clearServerLocaleCache(): void {
  if (typeof window === "undefined") {
    // 清除全局变量，强制重新获取
    delete (globalThis as Record<string, unknown>).__SERVER_LOCALE__;
  }
}

// 直接获取当前请求的语言（不依赖全局变量）
export function getCurrentRequestLocale(): Locale {
  if (typeof window === "undefined") {
    // 服务端：直接从 entry-server 传递的参数获取
    const requestLocale = (globalThis as Record<string, unknown>)
      .__CURRENT_REQUEST_LOCALE__;
    if (
      requestLocale &&
      typeof requestLocale === "string" &&
      Object.keys(LocaleMap).includes(requestLocale)
    ) {
      return requestLocale as Locale;
    }
    return DefaultLocale;
  }
  return currentLocale();
}

// 获取当前请求的语言参数（用于 API 调用）
export function getCurrentRequestLangParams(): {
  lang: number;
  locale: Locale;
} {
  const locale = getCurrentRequestLocale();
  return {
    lang: LocaleMap[locale],
    locale,
  };
}

// 获取当前请求的语言 key（用于 API 调用）
export function getCurrentRequestLangKey(): number {
  const locale = getCurrentRequestLocale();
  return LocaleMap[locale];
}

// 存储翻译内容
const [translations, setTranslations] = createSignal<
  Record<string, Record<string, unknown>>
>({});

// 添加加载状态
const [isLoading, setIsLoading] = createSignal(true);
const [isInitialized, setIsInitialized] = createSignal(true);
const [i18nInitialized, setI18nInitialized] = createSignal(true);

// 翻译缓存
const translationCache = new Map<
  Locale,
  Record<string, Record<string, unknown>>
>();

export const getLangConf = createRoot((dispose) => {
  const memo = createMemo(() => langConf[LocaleMap[currentLocale()]]);
  return memo;
});

export function ensureI18nInitializedServer() {
  // 初始化 i18next
  i18next
    .use(
      resourcesToBackend(async (lang: Locale) => {
        try {
          const common = await import(`./json/${lang}/common.json`);
          return common;
        } catch (error) {
          return {};
        }
      }),
    )
    .init({
      fallbackLng: DefaultLocale,
      // debug: process.env.NODE_ENV === "development",
      interpolation: {
        escapeValue: false, // SolidJS 已经默认转义
      },
      react: {
        useSuspense: false, // SSR 时禁用 Suspense
      },
    });
}

export function getLocaleByPath(pathname: string): Locale {
  const pathLocale = getLocaleFromPath(pathname);
  const detectedLocale =
    pathLocale && Object.keys(LocaleMap).includes(pathLocale)
      ? pathLocale
      : DefaultLocale;

  return detectedLocale;
}

export async function setLocale(lang: Locale) {
  if (!Object.keys(LocaleMap).includes(lang)) return;

  // Only set cookie on client side
  if (typeof window !== "undefined") {
    document.cookie = `i18n=${lang}; Path=/; SameSite=Lax`;
  }

  setCurrentLocale(lang);
  await changeLanguage(lang);
  updateUrlLocale(lang);
}

const memoCache = new Map<string, () => string>();

export function getTranslation(
  key: string,
  params?: Record<string, string | number>,
): string {
  const cacheKey = `${key}_${JSON.stringify(params || {})}`;

  if (!memoCache.has(cacheKey)) {
    const memo = createRoot(() =>
      // ✅ 包一层 createRoot，确保每个 memo 由根管理
      createMemo(() => {
        const currentLang = currentLocale();

        if (!i18nInitialized()) {
          // ensureI18nInitializedServer();
          return key;
        }

        const keys = key.split(".");
        let value: unknown = translations();

        for (const k of keys) {
          if (value && typeof value === "object") {
            value = (value as Record<string, unknown>)[k];
          } else {
            return key;
          }
        }

        if (typeof value === "string") {
          if (params) {
            return value.replace(/\{(\w+)\}/g, (match, p1) =>
              params[p1] !== undefined ? String(params[p1]) : match,
            );
          }
          return value;
        }
        return key;
      }),
    );

    memoCache.set(cacheKey, memo);
  }

  const memo = memoCache.get(cacheKey);
  return memo ? memo() : key;
}

// 获取翻译 - 使用全局计算管理器
export function t(
  key: string,
  params?: Record<string, string | number>,
): string {
  let value = i18next.t(key, params);

  if (typeof value === "string" && params) {
    value = value.replace(/\{(\w+)\}/g, (match, p1) =>
      params[p1] !== undefined ? String(params[p1]) : match,
    );
  }
  return value;
}

// 根据语言获取内容
export function getContentByLang(content: any): string {
  if (!content) return "";
  const langKey = getLangKey();
  return content[langKey] || "";
}

// 根据语言获取对象
export function getLangObject(content: any) {
  if (!content) return {};
  const langKey = getLangKey();
  return content[langKey] || {};
}

// 导出 i18next 实例
export { i18next };

// 导出 useI18n hook
export function useI18n() {
  return {
    t,
    i18n: i18next,
    isLoading: isLoading(),
    isInitialized: isInitialized(),
  };
}

// 语言切换hook
export function useLanguageSwitch() {
  const [isSwitching, setIsSwitching] = createSignal(false);

  const switchLanguage = async (lang: Locale) => {
    if (isSwitching()) return; // 防止重复切换

    try {
      setIsSwitching(true);
      await setLocale(lang);
    } catch (error) {
      console.error("Failed to switch language:", error);
    } finally {
      setIsSwitching(false);
    }
  };

  return {
    switchLanguage,
    isSwitching,
    currentLocale,
  };
}
export function useReactiveT() {
  return createMemo(() => {
    currentLocale();
    return (key: string, params?: Record<string, string | number>) =>
      t(key, params);
  });
}
